import { useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { NavigationBar } from '@/components/landing/NavigationBar';
import { ScrollProgressBar } from '@/components/landing/ScrollProgressBar';
import { HeroSection } from '@/components/landing/HeroSection';
import { SEOHead } from '@/components/seo/SEOHead';

const PromiseStripSection = lazy(() => import('@/components/landing/PromiseStripSection').then(m => ({ default: m.PromiseStripSection })));
const UniversityLogoStrip = lazy(() => import('@/components/landing/UniversityLogoStrip').then(m => ({ default: m.UniversityLogoStrip })));
const WhyVerithosSection = lazy(() => import('@/components/landing/WhyVerithosSection').then(m => ({ default: m.WhyVerithosSection })));
const WorkspacePeekSection = lazy(() => import('@/components/landing/WorkspacePeekSection').then(m => ({ default: m.WorkspacePeekSection })));

// Lazy-load below-the-fold sections
const HowItWorksSection = lazy(() => import('@/components/landing/HowItWorksSection').then(m => ({ default: m.HowItWorksSection })));

const WhoIsItForSection = lazy(() => import('@/components/landing/WhoIsItForSection').then(m => ({ default: m.WhoIsItForSection })));
const TestimonialsSection = lazy(() => import('@/components/landing/TestimonialsSection').then(m => ({ default: m.TestimonialsSection })));
const OutcomesSection = lazy(() => import('@/components/landing/OutcomesSection').then(m => ({ default: m.OutcomesSection })));
const CTASection = lazy(() => import('@/components/landing/CTASection').then(m => ({ default: m.CTASection })));
const FooterSection = lazy(() => import('@/components/landing/FooterSection').then(m => ({ default: m.FooterSection })));

export default function Index() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading]);

  useEffect(() => {
    if (!loading && user) {
      // Route new users directly to onboarding instead of /projects -> /onboarding chain
      supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .then(({ count }) => {
          const target = (count === 0) ? '/onboarding/start' : '/projects';
          navigate(target, { replace: true });
        });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    if (state?.scrollTo) {
      setTimeout(() => {
        document.getElementById(state.scrollTo!)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  if (loading || user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <div 
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.055) 0.5px, transparent 0)',
          backgroundSize: '22px 22px',
        }}
      />
      <SEOHead />
      <ScrollProgressBar />
      <NavigationBar />
      <main>
        <HeroSection />
        <PromiseStripSection />
        <UniversityLogoStrip />
        <WhyVerithosSection />
        <WorkspacePeekSection />
        <Suspense fallback={null}>
          <HowItWorksSection />
          <OutcomesSection />
          <WhoIsItForSection />
          <TestimonialsSection />
          <CTASection />
          <FooterSection />
        </Suspense>
      </main>
    </div>
  );
}
